import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { startSetEditProfile } from '../../actions/webmaster';
import SVG from 'react-inlinesvg';
import { STATIC_PATH } from '../../config';
import cn from 'classnames';
import {
  LIGHT_THEME,
  DARK_THEME,
  applyThemeClass,
  getEffectiveTheme,
  setStoredTheme,
} from '../../utils/themeStorage';

const ThemeToggle = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.webmaster.profile);
  const currentTheme = getEffectiveTheme(profile.theme);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    applyThemeClass(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const selectTheme = (themeId) => {
    if (loading || themeId === currentTheme) {
      setIsOpen(false);
      return;
    }

    setLoading(true);
    setStoredTheme(themeId);
    applyThemeClass(themeId);
    setIsOpen(false);

    // Best-effort sync with profile API. Backend may ignore unsupported theme ids;
    // UI theme is persisted in localStorage so it survives reload.
    dispatch(
      startSetEditProfile({
        ...profile,
        theme: themeId,
        auth_type: profile.auth_type || (profile.two_factor && profile.two_factor.type) || 0,
      }),
    )
      .catch(() => null)
      .then(() => {
        window.location.reload();
      });
  };

  return (
    <div className={cn('sidebar-theme-toggle-wrap', { open: isOpen, loading })} ref={rootRef}>
      <button
        type="button"
        className="sidebar-theme-toggle"
        onClick={() => setIsOpen((open) => !open)}
        disabled={loading}
        aria-expanded={isOpen}
      >
        <FormattedMessage id="sidebar.theme" defaultMessage="Тема" />
        <span className="sidebar-theme-toggle-value">
          {currentTheme === DARK_THEME ? (
            <FormattedMessage id="sidebar.theme.dark" defaultMessage="Тёмная" />
          ) : (
            <FormattedMessage id="sidebar.theme.light" defaultMessage="Светлая" />
          )}
          <SVG
            className="sidebar-theme-toggle-chevron"
            src={`${STATIC_PATH}/img/action-icons/arrow.svg`}
            height="12"
            width="12"
            cacheRequests
          />
        </span>
      </button>

      {isOpen && (
        <div className="sidebar-theme-menu" role="menu">
          <button
            type="button"
            role="menuitemradio"
            aria-checked={currentTheme === LIGHT_THEME}
            className={cn('sidebar-theme-menu-item', { active: currentTheme === LIGHT_THEME })}
            onClick={() => selectTheme(LIGHT_THEME)}
          >
            <FormattedMessage id="sidebar.theme.light" defaultMessage="Светлая" />
          </button>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={currentTheme === DARK_THEME}
            className={cn('sidebar-theme-menu-item', { active: currentTheme === DARK_THEME })}
            onClick={() => selectTheme(DARK_THEME)}
          >
            <FormattedMessage id="sidebar.theme.dark" defaultMessage="Тёмная" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;
